import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

import { MEDIUM, SMALL, useScreenSize } from "@swa-ui/browser";
import { Tabs } from "@swa-ui/core";
import { swaDate } from "@swa-ui/date";
import { ItineraryDate } from "@swa-ui/itinerary";
import i18n from "@swa-ui/locale";

const DEFAULT_NUMBER_OF_DAYS_TO_DISPLAY = 5;

/**
 * CalendarStrip is a date selection component that displays a horizontal strip of date tabs.
 * The strip can be configured to show a specific number of days and center on a selected date.
 * Past dates are not shown to prevent invalid selections.
 */

export const CalendarStrip = (props) => {
  const {
    centerStartDate = false,
    className,
    lastSelectableDate,
    numberOfDaysToDisplay = DEFAULT_NUMBER_OF_DAYS_TO_DISPLAY,
    onDateSelect,
    selectedStartDate,
  } = props;
  const { screenSize } = useScreenSize();
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const centerDateIndex = Math.floor(numberOfDaysToDisplay / 2);
  const isSmallOrMedium = screenSize === SMALL || screenSize === MEDIUM;

  useEffect(() => {
    const newIndex = getInitialSelectedTabIndex();

    setSelectedDateIndex(newIndex);
  }, [selectedStartDate, centerStartDate, centerDateIndex, numberOfDaysToDisplay]);

  return <Tabs {...getTabsProps()} />;

  /**
   * Calculates the intial selected index based on the actual tab list.
   * @returns {number} The index of the selected tab
   */
  function getInitialSelectedTabIndex() {
    let selectedIndex = 0;

    if (centerStartDate) {
      const today = swaDate();
      const centeredStartDate = swaDate(selectedStartDate).subtract(centerDateIndex, "day");

      if (centeredStartDate.isAfter(today) || centeredStartDate.isSame(today, "day")) {
        selectedIndex = centerDateIndex;
      } else {
        const startDate = getActualStartDate();
        const tabItems = getDateTabItems(startDate);
        const foundIndex = tabItems.findIndex((item) => item.key === selectedStartDate);

        selectedIndex = foundIndex >= 0 ? foundIndex : 0;
      }
    }

    return selectedIndex;
  }

  /**
   * Calculates the actual start date for the calendar strip and prevents past dates from being shown.
   * @returns {string} The formatted start date (YYYY-MM-DD)
   */
  function getActualStartDate() {
    const today = swaDate();
    let actualStartDate = !swaDate(selectedStartDate).isBefore(today, "day")
      ? selectedStartDate
      : today.format("YYYY-MM-DD");

    if (centerStartDate) {
      const centeredStartDate = swaDate(selectedStartDate).subtract(centerDateIndex, "day");

      actualStartDate =
        centeredStartDate.isAfter(today) || centeredStartDate.isSame(today, "day")
          ? centeredStartDate.format("YYYY-MM-DD")
          : today.format("YYYY-MM-DD");
    }

    return actualStartDate;
  }

  function getTabsProps() {
    const startDate = getActualStartDate();
    const items = getDateTabItems(startDate);

    return {
      "aria-label-next": i18n("CalendarStrip__TAB_NEXT"),
      "aria-label-previous": i18n("CalendarStrip__TAB_PREVIOUS"),
      animateContent: false,
      animateHeight: false,
      animateInitialRender: false,
      autoFit: true,
      className,
      items,
      onChange: handleTabChange,
      paddingContent: "none",
      selectedIndex: selectedDateIndex,
    };
  }

  function getDateTabItems(startDate) {
    const startSwaDate = swaDate(startDate);

    return Array.from({ length: numberOfDaysToDisplay }, (_, index) => {
      const currentDate = swaDate(startSwaDate).add(index, "day");
      const ariaFormattedDate = currentDate.format("dddd MMMM D");
      const formattedDate = currentDate.format("YYYY-MM-DD");
      const dateIsDisabled = lastSelectableDate
        ? currentDate.isAfter(lastSelectableDate, "day")
        : false;

      return {
        "aria-label": ariaFormattedDate,
        content: null,
        disabled: dateIsDisabled,
        key: formattedDate,
        label: (
          <>
            <ItineraryDate {...getTopDateProps(formattedDate)} />
            <ItineraryDate {...getBottomDateProps(formattedDate)} />
          </>
        ),
      };
    });
  }

  function getTopDateProps(date) {
    return {
      date,
      format: "ddd",
      textProps: {
        size: isSmallOrMedium ? "small" : "xlarge",
      },
    };
  }

  function getBottomDateProps(date) {
    return {
      date,
      format: "MMM D",
      textProps: {
        bold: true,
        size: isSmallOrMedium ? "small" : "xlarge",
      },
    };
  }

  function handleTabChange(tabIndex) {
    const startDate = getActualStartDate();
    const startSwaDate = swaDate(startDate);
    const selectedDate = startSwaDate.add(tabIndex, "day").format("YYYY-MM-DD");

    onDateSelection(tabIndex, selectedDate);
  }

  function onDateSelection(dateIndex, selectedDate) {
    if (dateIndex !== selectedDateIndex) {
      setSelectedDateIndex(dateIndex);
      onDateSelect?.(selectedDate);
    }
  }
};

CalendarStrip.propTypes = {
  /** If true, renders the selectedStartDate in the center of numberOfDaysToDisplay. */
  centerStartDate: PropTypes.bool,

  /** Classes for positioning the component. */
  className: PropTypes.string,

  /** The last date that can be selected in the calendar strip. Dates after this will be disabled. */
  lastSelectableDate: PropTypes.string,

  /** The number of days to display in the strip. */
  numberOfDaysToDisplay: PropTypes.number,

  /** Function called on clicking on a date tab. Returns the formatted date. */
  onDateSelect: PropTypes.func,

  /** The initial date to be selected. This should be formatted like so: 02/29/2024, 2024-2-29, etc. */
  selectedStartDate: PropTypes.string.isRequired,
};
